import { Box, Select, Option, Typography, Button, IconButton, Stack, Input } from "@mui/joy";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";
import { Building2, Plus, X, Ruler, Palette, Hash } from "lucide-react";

interface IChimneyList {
    value: string | null;
    onChange: (value: string) => void;
    loading?: boolean;
}

interface ChimneyEntry {
    type: string;
    size: string;
    color: string;
    count: number;
}

/**
 * Repeatable list for multiple chimneys.
 * Stores as JSON array: [{"type":"Brick","size":"Small","color":"Red","count":1}]
 */
export function ChimneyList(props: IChimneyList) {
    const { value, onChange, loading } = props;

    const parseValue = (val: string | null): ChimneyEntry[] => {
        if (!val) return [{ type: "N/A", size: "None", color: "", count: 1 }];
        try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch {
            // Basic fallback for non-JSON values
        }
        return [{ type: "N/A", size: "None", color: "", count: 1 }];
    };

    const [entries, setEntries] = useState<ChimneyEntry[]>(() => parseValue(value));

    const debouncedOnChange = useRef(
        debounce((updatedEntries: ChimneyEntry[]) => {
            onChange(JSON.stringify(updatedEntries));
        }, 500)
    ).current;

    useEffect(() => {
        return () => { debouncedOnChange.cancel(); };
    }, [debouncedOnChange]);

    useEffect(() => {
        if (value) {
            setEntries(parseValue(value));
        }
    }, [value]);

    const handleUpdate = (index: number, field: keyof ChimneyEntry, val: any) => {
        if (loading) return;
        const updated = entries.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry));
        setEntries(updated);
        debouncedOnChange(updated);
    };

    const addEntry = () => {
        setEntries((prev) => [...prev, { type: "Brick", size: "Average (32x36)", color: "", count: 1 }]);
    };

    const removeEntry = (index: number) => {
        if (entries.length <= 1) {
            const reset = [{ type: "N/A", size: "None", color: "", count: 0 }];
            setEntries(reset);
            debouncedOnChange(reset);
            return;
        }
        const updated = entries.filter((_, i) => i !== index);
        setEntries(updated);
        debouncedOnChange(updated);
    };

    return (
        <Box>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 1 }}>
                Chimneys
            </Typography>
            <Stack spacing={2}>
                {entries.map((entry, i) => (
                    <Box key={i} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 'sm', bgcolor: 'background.surface', position: 'relative' }}>
                        <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={() => removeEntry(i)}
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                        >
                            <X size={16} />
                        </IconButton>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Type</Typography>
                                <Select
                                    size="sm"
                                    value={entry.type}
                                    onChange={(_e, val) => handleUpdate(i, "type", val)}
                                    startDecorator={<Building2 size={14} />}
                                >
                                    <Option value="N/A">N/A</Option>
                                    <Option value="Brick">Brick</Option>
                                    <Option value="Siding">Siding</Option>
                                </Select>
                            </Box>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Size</Typography>
                                <Select
                                    size="sm"
                                    value={entry.size}
                                    onChange={(_e, val) => handleUpdate(i, "size", val)}
                                    startDecorator={<Ruler size={14} />}
                                >
                                    <Option value="None">None</Option>
                                    <Option value="Small (24x24)">Small (24"×24")</Option>
                                    <Option value="Average (32x36)">Average (32"×36")</Option>
                                    <Option value="Large (32x60)">Large (32"×60")</Option>
                                </Select>
                            </Box>
                            <Box sx={{ flex: '1 1 150px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Color</Typography>
                                <Input
                                    size="sm"
                                    placeholder="Color"
                                    value={entry.color}
                                    onChange={(e) => handleUpdate(i, "color", e.target.value)}
                                    startDecorator={<Palette size={14} />}
                                />
                            </Box>
                            <Box sx={{ flex: '0 1 80px' }}>
                                <Typography level="body-xs" sx={{ mb: 0.5 }}>Count</Typography>
                                <Input
                                    size="sm"
                                    type="number"
                                    value={entry.count}
                                    onChange={(e) => handleUpdate(i, "count", Number(e.target.value))}
                                    startDecorator={<Hash size={14} />}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
                <Button
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    startDecorator={<Plus size={16} />}
                    onClick={addEntry}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    Add Chimney
                </Button>
            </Stack>
        </Box>
    );
}
